import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../../lib/cn.js";
import { Field, controlClass, controlInvalidClass } from "./Field.js";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  icon?: ReactNode;
  suffix?: ReactNode;
  /** Números tabulares y alineado a la derecha (importes, DNI, números de socio). */
  numeric?: boolean;
  /**
   * El ojo para ver lo que se está tipeando. Nace prendido en todo `type="password"`:
   * una contraseña que no se puede leer se escribe mal, y la que más se escribe mal es
   * la que alguien está creando. Apagalo (`revelable={false}`) sólo donde mostrarla
   * sería un riesgo real, por ejemplo un PIN tipeado de cara al mostrador.
   */
  revelable?: boolean;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    icon,
    suffix,
    numeric,
    revelable = true,
    required,
    className,
    containerClassName,
    ...rest
  },
  ref,
) {
  const [revelado, setRevelado] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loAlterno = useRef(false);

  // El sufijo ya ocupa la derecha del campo; ahí el ojo se le encimaría.
  const conOjo = rest.type === "password" && revelable && !suffix;

  useEffect(() => {
    // Sólo cuando la persona apretó el ojo: al montar, el foco es de quien lo pidió.
    if (!loAlterno.current) return;
    const input = inputRef.current;
    if (!input) return;
    // Cambiar `type` reinicia la selección: el cursor vuelve al final para seguir tipeando.
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [revelado]);

  return (
    <Field
      {...(label !== undefined ? { label } : {})}
      {...(hint !== undefined ? { hint } : {})}
      {...(error !== undefined ? { error } : {})}
      {...(required !== undefined ? { required } : {})}
      {...(containerClassName !== undefined ? { className: containerClassName } : {})}
    >
      {({ id, describedBy, invalid }) => (
        <div className="relative flex items-center">
          {icon ? (
            <span className="pointer-events-none absolute left-3 text-mute [&>svg]:size-4">
              {icon}
            </span>
          ) : null}
          <input
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === "function") ref(node);
              else if (ref) ref.current = node;
            }}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            className={cn(
              controlClass,
              "h-9 text-[13px]",
              icon && "pl-9",
              suffix && "pr-10",
              conOjo && "pr-9",
              numeric && "text-right [font-variant-numeric:tabular-nums]",
              invalid && controlInvalidClass,
              className,
            )}
            {...rest}
            type={conOjo && revelado ? "text" : rest.type}
          />
          {suffix ? (
            <span className="pointer-events-none absolute right-3 text-[12px] text-mute">
              {suffix}
            </span>
          ) : null}
          {conOjo ? (
            <button
              // Dentro de un formulario el default es `submit`: sin esto, ver la
              // contraseña mandaría el login.
              type="button"
              onClick={() => {
                loAlterno.current = true;
                setRevelado((v) => !v);
              }}
              // Dice la acción que va a pasar, no el estado: es lo que hace falta saber
              // antes de apretarlo.
              aria-label={revelado ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              title={revelado ? "Ocultar la contraseña" : "Mostrar la contraseña"}
              className="absolute right-0 grid h-9 w-9 place-items-center rounded-r-[10px] text-mute hover:text-fg [&>svg]:size-4"
            >
              {revelado ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
            </button>
          ) : null}
        </div>
      )}
    </Field>
  );
});
